if (!customElements.get('quick-order-form')) {
  customElements.define(
    'quick-order-form',
    class QuickOrderList extends HTMLFormElement {
      static DEBOUNCE_TIME = 1000;
      static LOADING_CLASS = 'btn--loading';
      static STATUS_TIMEOUT = 3000;
      static STATUS_ANIMATION_MS = 350;

      constructor() {
        super();
        this.cartUpdateUnsubscriber = undefined;
        this.cartClearUnsubscriber = undefined;
        this._boundHandleFormSubmit = (e) => this.handleFormSubmit(e);
        this.initializeComponent();
      }

      /**
       * Initialize the component with necessary event listeners and data
       */
      initializeComponent() {
        this.addEventListener(
          'change',
          FoxTheme.utils.debounce(this.handleQuantityChange.bind(this), QuickOrderList.DEBOUNCE_TIME)
        );

        this._boundHandleQuantityChangeStart = (event) => this.handleQuantityChangeStart(event);
        this.addEventListener('change', this._boundHandleQuantityChangeStart);

        this.addEventListener('submit', this._boundHandleFormSubmit);
        this._boundHandleFormClick = (e) => this.handleFormClick(e);
        this.addEventListener('click', this._boundHandleFormClick);

        this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
          this.handleCartUpdate.bind(this)
        );

        this.cartClearUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.cartClear,
          this.handleCartClear.bind(this)
        );

        this.sectionId = this.getAttribute('data-section-id');
        this.variantIds = this.querySelectorAll('[data-quantity-variant-id]');
      }

      /**
       * Clean up event listeners when component is removed
       */
      disconnectedCallback() {
        this.removeEventListener('submit', this._boundHandleFormSubmit);
        if (this._boundHandleFormClick) {
          this.removeEventListener('click', this._boundHandleFormClick);
        }
        if (this._boundHandleQuantityChangeStart) {
          this.removeEventListener('change', this._boundHandleQuantityChangeStart);
        }
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
          this.cartUpdateUnsubscriber = null;
        }
      }

      /**
       * Prevent form submit so quantity updates use fetch only (no page reload)
       * @param {Event} event - Submit event
       */
      handleFormSubmit(event) {
        event.preventDefault();
      }

      /**
       * Delegate remove button clicks (works for initial and dynamically loaded rows)
       * @param {Event} event - Click event
       */
      handleFormClick(event) {
        const removeBtn = event.target.closest('.quick-order-list__remove[data-index]');
        if (!removeBtn) return;
        event.preventDefault();
        const itemIndex = removeBtn.getAttribute('data-index');
        if (itemIndex) {
          this.updateItemQuantity(itemIndex, 0);
        }
      }

      /**
       * Show the row subtotal skeleton as soon as quantity changes (before debounce/fetch)
       * @param {Event} event - Change event
       */
      handleQuantityChangeStart(event) {
        const { target } = event;
        if (!target || !target.classList.contains('quantity__input')) return;
        const index = target.getAttribute('data-index');
        if (index) this.toggleLineTotalSkeleton(index, true);
      }

      /**
       * Handle quantity change events
       * @param {Event} event - Change event
       */
      handleQuantityChange(event) {
        const { target } = event;
        const inputValue = parseInt(target.value);
        const index = target.getAttribute('data-index');

        if (inputValue === 0) {
          this.updateItemQuantity(index, inputValue, document.activeElement.getAttribute('name'), target);
        } else {
          this.validateQuantityInput(event);
        }
      }

      /**
       * Validate quantity input against min, max, and step constraints
       * @param {Event} event - Change event
       */
      validateQuantityInput(event) {
        const { target } = event;
        const inputValue = parseInt(target.value);
        const index = target.getAttribute('data-index');
        const minValue = parseInt(target.getAttribute('data-min'));
        const maxValue = parseInt(target.max);
        const stepValue = parseInt(target.step);

        const validationRules = [
          {
            isInvalid: inputValue < minValue,
            message: FoxTheme.quickOrderListStrings.minError.replace('[min]', minValue),
          },
          {
            isInvalid: inputValue > maxValue,
            message: FoxTheme.quickOrderListStrings.maxError.replace('[max]', maxValue),
          },
          {
            isInvalid: inputValue % stepValue !== 0,
            message: FoxTheme.quickOrderListStrings.stepError.replace('[step]', stepValue),
          },
        ];

        const failedValidation = validationRules.find((rule) => rule.isInvalid);

        if (failedValidation) {
          this.setInputValidity(target, failedValidation.message);
          return;
        }

        target.setCustomValidity('');
        target.reportValidity();
        this.updateItemQuantity(index, inputValue, document.activeElement.getAttribute('name'), target);
      }

      /**
       * Set input validity and show error message
       * @param {HTMLElement} input - Input element
       * @param {string} message - Error message
       */
      setInputValidity(input, message) {
        input.setCustomValidity(message);
        input.reportValidity();
        input.value = input.defaultValue;
        input.select();
        const index = input.getAttribute('data-index');
        if (index) this.toggleLineTotalSkeleton(index, false);
        this.toggleSummarySkeleton(false);
      }

      /**
       * Update single item quantity
       * @param {string} line - Line item index
       * @param {number} quantity - New quantity
       * @param {string} name - Input name
       * @param {HTMLElement} target - Target element
       */
      updateItemQuantity(line, quantity, name, target) {
        const items = {};
        items[line] = quantity;
        console.log("Update item quantity", line, quantity, name, target);
        this.updateCartItems(items, line, name, target);
      }

      /**
       * Update multiple cart items
       * @param {Object} items - Items to update
       * @param {string} line - Line item index
       * @param {string} name - Input name
       * @param {HTMLElement} target - Target element
       */
      updateCartItems(items, line, name, target) {
        this.toggleLoadingState(line, true);
        this.toggleLineTotalSkeleton(line, true);
        this.showErrorMessage();

        // Request section HTML for cart drawer (and others) so they can update; we still update quick-order row/total via JS only
        const sectionsToUpdate = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', {
            bubbles: true,
            detail: { sections: sectionsToUpdate },
          })
        );

        const requestBody = JSON.stringify({
          updates: items,
          sections: sectionsToUpdate,
          sections_url: this.getAttribute('data-product-url') || window.location.pathname,
        });

        fetch(`${FoxTheme.routes.cart_update_url}`, {
          ...FoxTheme.utils.fetchConfig(),
          body: requestBody,
        })
          .then((response) =>
            response.json().then((data) => ({ ok: response.ok, status: response.status, data }))
          )
          .then(({ ok, status, data }) => {
            if (!ok) {
              const message = (data && (data.message || data.description)) || 'Cart update failed';
              throw new Error(message);
            }
            this.handleCartUpdateSuccess(data, target, line, name);
          })
          .catch((error) => this.handleCartUpdateError(error))
          .finally(() => this.toggleLoadingState(line, false));
      }

      /**
       * Handle successful cart update
       * @param {Object} data - Cart data
       * @param {HTMLElement} target - Target element
       * @param {string} line - Line item index
       * @param {string} name - Input name
       */
      handleCartUpdateSuccess(data, target, line, name) {
        if (!data || !Array.isArray(data.items)) {
          this.handleCartUpdateError(new Error('Invalid cart response'));
          return;
        }
        const quantityInput = this.querySelector(`.quantity__input[data-index="${line}"]`);
        const previousQuantity = quantityInput
          ? parseInt(quantityInput.getAttribute('data-cart-quantity'), 10) || 0
          : 0;
        const lineItem = data.items.find((item) => item.variant_id == line);
        const newQuantity = lineItem ? lineItem.quantity : 0;
        const newLineTotal = FoxTheme.Currency.formatMoney(lineItem ? lineItem.line_price : 0);

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'quick-order-list',
          cart: { ...data, sections: data.sections || {} },
          target,
          line,
          name,
        });

        this.querySelectorAll('.quantity__input[data-index="' + line + '"]').forEach((input) => {
          input.value = newQuantity;
          input.setAttribute('data-cart-quantity', newQuantity);
        });

        const variantRow = document.getElementById(`VariantItem-${this.sectionId}-${line}`);
        if (variantRow) {
          const totalEls = this.updateLineTotalDisplay(variantRow, newLineTotal);
          this.toggleLineTotalSkeleton(line, false);
          setTimeout(() => this.pulseElements(totalEls), 250);
          this.showRowFeedback(variantRow, previousQuantity, newQuantity);
        }

        const mainEl = document.getElementById(`MainQuickOrderList-${this.sectionId}`);
        if (mainEl) {
          this.updateCartSummaries(mainEl, data);
          this.showCartUpdatedStatus(mainEl);
        }

        if (newQuantity === 0) {
          this.querySelectorAll('.quick-order-list__remove[data-index="' + line + '"]').forEach((button) => {
            button.classList.add('invisible');
            button.classList.add('pointer-events-none');
            button.classList.remove('flex');
          });
        } else {
          this.querySelectorAll('.quick-order-list__remove[data-index="' + line + '"]').forEach((button) => {
            button.classList.remove('invisible');
            button.classList.remove('pointer-events-none');
            button.classList.add('flex');
          });
        }
      }

      formatItemsCount(count) {
        const template =
          count === 1
            ? FoxTheme.quickOrderListStrings.itemsCountOne
            : FoxTheme.quickOrderListStrings.itemsCountOther;
        return template.replace('[count]', String(count));
      }

      updateCartSummaries(mainEl, data) {
        const itemCount = data.item_count || 0;
        const cartTotal = FoxTheme.Currency.formatMoney(data.total_price || 0);

        mainEl.querySelectorAll('.quick-order-list__total .totals__subtotal-value').forEach((el) => {
          el.textContent = cartTotal;
        });
        mainEl.querySelectorAll('.quick-order-list__total-items-count').forEach((el) => {
          el.textContent = itemCount;
        });

        const confirmSpan = mainEl.querySelector('.quick-order-list-total__confirmation > span');
        if (confirmSpan) {
          confirmSpan.textContent = confirmSpan.textContent.replace(/\d+/, String(itemCount));
        }

        mainEl.querySelectorAll('.quick-order-floating-summary__count').forEach((el) => {
          el.textContent = this.formatItemsCount(itemCount);
        });
        mainEl.querySelectorAll('.quick-order-floating-summary__total').forEach((el) => {
          el.textContent = cartTotal;
        });
        const viewCartWithTotal = FoxTheme.quickOrderListStrings.viewCartWithTotal.replace('[total]', cartTotal);
        mainEl.querySelectorAll('.quick-order-floating-summary__button-text-desktop').forEach((el) => {
          el.textContent = viewCartWithTotal;
        });

        mainEl.querySelectorAll('.quick-order-floating-summary__button').forEach((button) => {
          const isEmpty = itemCount === 0;
          button.classList.toggle('is-disabled', isEmpty);
          button.toggleAttribute('aria-disabled', isEmpty);
          if (isEmpty) {
            button.setAttribute('tabindex', '-1');
          } else {
            button.removeAttribute('tabindex');
          }
        });

        mainEl.querySelectorAll('quick-order-list-remove-all[data-action="confirm"]').forEach((button) => {
          button.hidden = itemCount === 0;
        });

        this.toggleSummarySkeleton(false);

        const summaryTotals = [
          ...mainEl.querySelectorAll('.quick-order-list__total .totals__subtotal-value'),
          ...mainEl.querySelectorAll('.quick-order-floating-summary__total'),
        ];
        this.pulseElements([
          ...mainEl.querySelectorAll('.quick-order-list__total-items-count'),
          ...mainEl.querySelectorAll('.quick-order-list__button'),
          ...mainEl.querySelectorAll('.quick-order-floating-summary__count'),
          ...mainEl.querySelectorAll('.quick-order-floating-summary__button'),
        ]);
        setTimeout(() => this.pulseElements(summaryTotals), 250);
      }

      showCartUpdatedStatus(mainEl) {
        const statusEls = [
          ...mainEl.querySelectorAll('.quick-order-list__cart-status'),
          ...mainEl.querySelectorAll('.quick-order-floating-summary__status'),
        ];
        statusEls.forEach((el) => this.revealStatus(el));
      }

      showRowFeedback(variantRow, previousQuantity, newQuantity) {
        const statusEl =
          variantRow.nextElementSibling?.classList.contains('variant-item__status')
            ? variantRow.nextElementSibling
            : variantRow.querySelector('.variant-item__status');
        const messageEl = statusEl && statusEl.querySelector('.variant-item__status-message');
        if (!statusEl || !messageEl) return;

        if (previousQuantity === 0 && newQuantity > 0) {
          const template =
            newQuantity === 1
              ? FoxTheme.quickOrderListStrings.itemAdded
              : FoxTheme.quickOrderListStrings.itemsAdded;
          messageEl.textContent = template.replace('[quantity]', String(newQuantity));
        } else {
          messageEl.textContent = FoxTheme.quickOrderListStrings.quantityUpdated;
        }

        this.revealStatus(statusEl);
      }

      revealStatus(el) {
        this.cancelStatusHide(el);
        el.hidden = false;
        void el.offsetHeight;
        requestAnimationFrame(() => {
          el.classList.add('is-visible');
        });
        el._statusTimeout = setTimeout(() => this.concealStatus(el), QuickOrderList.STATUS_TIMEOUT);
      }

      concealStatus(el) {
        this.cancelStatusHide(el);
        el.classList.remove('is-visible');
        const done = (event) => {
          if (event && event.target !== el && !el.contains(event.target)) return;
          if (event && event.propertyName && event.propertyName !== 'opacity') return;
          this.finishStatusHide(el, done);
        };
        el._onStatusTransitionEnd = done;
        el.addEventListener('transitionend', done);
        el._statusHideTimeout = setTimeout(() => this.finishStatusHide(el, done), QuickOrderList.STATUS_ANIMATION_MS);
      }

      finishStatusHide(el, done) {
        el.hidden = true;
        el.classList.remove('is-visible');
        if (done) el.removeEventListener('transitionend', done);
        el._onStatusTransitionEnd = null;
        clearTimeout(el._statusHideTimeout);
      }

      cancelStatusHide(el) {
        clearTimeout(el._statusTimeout);
        clearTimeout(el._statusHideTimeout);
        if (el._onStatusTransitionEnd) {
          el.removeEventListener('transitionend', el._onStatusTransitionEnd);
          el._onStatusTransitionEnd = null;
        }
      }

      pulseElements(elements) {
        elements.forEach((el) => {
          if (!el) return;
          el.classList.remove('quick-order-is-updated');
          void el.offsetWidth;
          el.classList.add('quick-order-is-updated');
          const onEnd = () => {
            el.classList.remove('quick-order-is-updated');
            el.removeEventListener('animationend', onEnd);
          };
          el.addEventListener('animationend', onEnd);
        });
      }

      /**
       * Handle cart update errors
       * @param {Error} error - Error object
       */
      handleCartUpdateError(error) {
        if (error.name === 'AbortError') {
          console.log('Request cancelled');
        } else {
          console.error('Cart update error:', error);
          const message = (error && error.message) || (FoxTheme.cartStrings && FoxTheme.cartStrings.error) || 'Cart update failed';
          this.showErrorMessage(message);
        }
      }

      /**
       * Handle cart update event
       * @param {CustomEvent} event - Cart update event
       */
      handleCartUpdate(event) {
        if (event.cart.errors) {
          this.handleCartError(event.cart.errors, event.target);
          return;
        }

        // Only update if the cart update is from a different source (e.g., cart drawer)
        // to avoid double updates when we update from quick-order-list itself
        if (event.source !== 'quick-order-list' && event.cart && event.cart.items) {
          this.updateQuantitiesFromCart(event.cart);
        }

        //this.updateCartSections(sectionContent, event);
        this.updateFocus(event);
        this.dispatchCartUpdateEvent(event.cart);
      }

      /**
       * Update all quantity inputs based on cart state
       * @param {Object} cart - Cart data
       */
      updateQuantitiesFromCart(cart) {
        const cartItems = cart.items || [];
        const sectionId = this.sectionId;

        // Create a map of variant ID to quantity and line price for quick lookup
        const variantDataMap = {};
        cartItems.forEach((item) => {
          const variantId = item.variant_id ? parseInt(item.variant_id) : null;
          if (variantId) {
            variantDataMap[variantId] = {
              quantity: item.quantity || 0,
              linePrice: item.line_price || 0,
            };
          }
        });

        // Update all quantity inputs
        this.querySelectorAll('.quantity__input[data-quantity-variant-id]').forEach((input) => {
          const variantId = parseInt(input.getAttribute('data-quantity-variant-id'));
          const variantData = variantDataMap[variantId];
          const newQuantity = variantData ? variantData.quantity : 0;
          const currentValue = parseInt(input.value) || 0;

          if (currentValue !== newQuantity) {
            input.value = newQuantity;
            // Update data-cart-quantity attribute
            input.setAttribute('data-cart-quantity', newQuantity);
          }

          // Update remove button visibility
          const lineId = input.getAttribute('data-index');
          const removeButtons = this.querySelectorAll(`.quick-order-list__remove[data-index="${lineId}"]`);
          
          if (newQuantity === 0) {
            removeButtons.forEach((button) => {
              button.classList.add('invisible');
              button.classList.add('pointer-events-none');
              button.classList.remove('flex');
            });
          } else {
            removeButtons.forEach((button) => {
              button.classList.remove('invisible');
              button.classList.remove('pointer-events-none');
              button.classList.add('flex');
            });
          }

          // Update line total if element exists (desktop and mobile)
          const variantItem = document.getElementById(`VariantItem-${sectionId}-${variantId}`);
          if (variantItem && variantData) {
            this.updateLineTotalDisplay(variantItem, FoxTheme.Currency.formatMoney(variantData.linePrice));
          } else if (variantItem && newQuantity === 0) {
            this.updateLineTotalDisplay(variantItem, FoxTheme.Currency.formatMoney(0));
          }
        });

        // Update cart totals
        const mainQuickOrderList = document.getElementById(`MainQuickOrderList-${sectionId}`);
        if (mainQuickOrderList) {
          this.updateCartSummaries(mainQuickOrderList, cart);
          this.showCartUpdatedStatus(mainQuickOrderList);
        }
      }

      /**
       * Handle cart clear event
       * @param {CustomEvent} event - Cart clear event
       */
      handleCartClear(event) {
        if (event.cart.errors) {
          this.handleCartError(event.cart.errors, event.target);
          return;
        }

        this.querySelectorAll('quantity-input').forEach((item) => {
          item.querySelector('input').value = 0;
        });

        this.querySelectorAll('.variant-item__totals-value').forEach((item) => {
          item.textContent = FoxTheme.Currency.formatMoney(0);
        });
        this.querySelectorAll('.variant-item').forEach((row) => {
          row.classList.remove('is-updating');
        });

        const mainEl = document.getElementById(`MainQuickOrderList-${this.sectionId}`);
        if (mainEl) {
          this.updateCartSummaries(mainEl, { item_count: 0, total_price: 0 });
        }

        //this.updateCartSections(sectionContent, event);
        this.updateFocus(event);
        this.dispatchCartClearEvent(event.cart);
      }

      /**
       * Update cart sections in DOM
       * @param {Document} sectionContent - Parsed section content
       * @param {CustomEvent} event - Cart update event
       */
      updateCartSections(sectionContent, event) {
        const cartDrawer = document.querySelector(`#CartDrawer-${this.sectionId}`);
        const mainQuickOrderList = document.querySelector(`#MainQuickOrderList-${this.sectionId}`);

        if (cartDrawer) {
          const newDrawerContent = sectionContent.querySelector(`#CartDrawer-${this.sectionId}`);
          if (newDrawerContent) {
            cartDrawer.innerHTML = newDrawerContent.innerHTML;
          }
        }

        if (mainQuickOrderList) {
          const newListContent = sectionContent.querySelector(`#MainQuickOrderList-${this.sectionId}`);
          if (newListContent) {
            mainQuickOrderList.innerHTML = newListContent.innerHTML;
          }
        }
      }

      /**
       * Update focus after cart update
       * @param {CustomEvent} event - Cart update event
       */
      updateFocus(event) {
        const mainQuickOrderList = document.querySelector(`#MainQuickOrderList-${this.sectionId}`);
        const lineItem = document.getElementById(`VariantItem-${this.sectionId}-${event.line}`);

        if (lineItem && lineItem.querySelector(`[name="${event.name}"]`)) {
          FoxTheme.a11y.trapFocus(mainQuickOrderList, lineItem.querySelector(`[name="${event.name}"]`));
        } else {
          FoxTheme.a11y.trapFocus(mainQuickOrderList, mainQuickOrderList.querySelector('.variant-item__title'));
        }
      }

      /**
       * Dispatch cart updated event
       * @param {Object} cart - Cart data
       */
      dispatchCartUpdateEvent(cart) {
        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: { cart },
          })
        );
        console.log('Cart updated:', cart);
      }

      /**
       * Dispatch cart cleared event
       * @param {Object} cart - Cart data
       */
      dispatchCartClearEvent(cart) {
        document.dispatchEvent(
          new CustomEvent('cart:cleared', {
            detail: { cart },
          })
        );
        console.log('Cart cleared:', cart);
      }

      /**
       * Handle cart errors
       * @param {string} errors - Error message
       * @param {HTMLElement} target - Target element
       */
      handleCartError(errors, target) {
        if (!target) {
          window.location.href = FoxTheme.routes.cart_url;
          return;
        }

        this.toggleLoadingState(target.getAttribute('data-index'), false);
        this.setInputValidity(target, errors);
      }

      /**
       * Toggle loading state for elements
       * @param {string} line - Line item index
       * @param {boolean} isLoading - Loading state
       */
      toggleLoadingState(line, isLoading) {
        const loaders = document.querySelectorAll(`#Loader-${this.sectionId}-${line}`);
        const allLoader = document.getElementById(`Loader-${this.sectionId}-all`);

        const updateLoader = (element) => {
          if (!element) return;
          element.classList[isLoading ? 'add' : 'remove'](QuickOrderList.LOADING_CLASS);
          element.hidden = !isLoading;
        };

        if (loaders) {
          loaders.forEach(updateLoader);
        }

        if (allLoader) {
          updateLoader(allLoader);
        }

        if (!isLoading) {
          this.toggleLineTotalSkeleton(line, false);
          this.toggleSummarySkeleton(false);
        }
      }

      /**
       * Show or hide the suspense skeleton on cart summary totals
       * @param {boolean} isLoading - Whether summaries are awaiting a refresh
       */
      toggleSummarySkeleton(isLoading) {
        const mainEl = document.getElementById(`MainQuickOrderList-${this.sectionId}`);
        if (!mainEl) return;
        mainEl.classList.toggle('is-updating-summaries', isLoading);
        mainEl.querySelectorAll('.quick-order-skeleton-wrap').forEach((el) => {
          el.setAttribute('aria-busy', isLoading ? 'true' : 'false');
        });
      }

      /**
       * Show or hide the suspense skeleton on a row subtotal
       * @param {string} line - Variant id
       * @param {boolean} isLoading - Whether the subtotal is awaiting a refresh
       */
      toggleLineTotalSkeleton(line, isLoading) {
        const variantRow = document.getElementById(`VariantItem-${this.sectionId}-${line}`);
        if (!variantRow) return;

        if (!isLoading) {
          const input = this.querySelector(`.quantity__input[data-index="${line}"]`);
          const current = input ? parseInt(input.value, 10) || 0 : 0;
          const committed = input ? parseInt(input.getAttribute('data-cart-quantity'), 10) || 0 : 0;
          if (current !== committed) return;
        }

        variantRow.classList.toggle('is-updating', isLoading);
        variantRow.querySelectorAll('.variant-item__totals-wrap').forEach((el) => {
          el.setAttribute('aria-busy', isLoading ? 'true' : 'false');
        });
        if (isLoading) this.toggleSummarySkeleton(true);
      }

      /**
       * Update desktop and mobile line totals without replacing the skeleton markup
       * @param {HTMLElement} variantRow - Product row
       * @param {string} formattedTotal - Formatted money string
       * @returns {HTMLElement[]} Updated value elements
       */
      updateLineTotalDisplay(variantRow, formattedTotal) {
        if (!variantRow) return [];
        const values = [...variantRow.querySelectorAll('.variant-item__totals-value')];
        if (values.length) {
          values.forEach((el) => {
            el.textContent = formattedTotal;
          });
          return values;
        }

        const fallbacks = [
          variantRow.querySelector('.variant-item__totals'),
          ...variantRow.querySelectorAll('.variant-item__line-total'),
        ].filter(Boolean);
        fallbacks.forEach((el) => {
          el.textContent = formattedTotal;
        });
        return fallbacks;
      }

      /**
       * Show/hide error message
       * @param {string|null} message - Error message
       */
      showErrorMessage(message = null) {
        const errorElement = this.querySelector('.quick-order-list__error');
        if (!errorElement) return;

        if (message) {
          const messageElement = errorElement.querySelector('.quick-order-list-error__message');
          if (messageElement) {
            messageElement.textContent = message;
          }
          errorElement.removeAttribute('hidden');
        } else {
          errorElement.setAttribute('hidden', '');
        }
      }
    },
    { extends: 'form' }
  );
}

if (!customElements.get('quick-order-form-wrapper')) {
  customElements.define(
    'quick-order-form-wrapper',
    class QuickOrderList extends HTMLElement {
      constructor() {
        super();
        this._sectionId = this.getAttribute('data-section-id');
        this.bindEvents();
      }

      bindEvents() {
        this._handleGroupedSections = (event) => this.getSectionToBundle(event);
        document.addEventListener('cart:grouped-sections', this._handleGroupedSections);
      }

      getSectionToBundle(event) {
        if (event.detail && Array.isArray(event.detail.sections)) {
          event.detail.sections.push(this._sectionId);
        }
      }

      disconnectedCallback() {
        if (this._handleGroupedSections) {
          document.removeEventListener('cart:grouped-sections', this._handleGroupedSections);
          this._handleGroupedSections = null;
        }
      }
    }
  );
}

if (!customElements.get('quick-order-floating-summary')) {
  customElements.define(
    'quick-order-floating-summary',
    class QuickOrderFloatingSummary extends HTMLElement {
      connectedCallback() {
        this.summary = this.closest('quick-order-form-wrapper')?.querySelector(
          this.dataset.summarySelector || '.quick-order-list__total'
        );
        if (!this.summary) return;

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.toggle(entry.isIntersecting === false);
            });
          },
          { threshold: 0, rootMargin: '-80px 0px 0px 0px' }
        );
        this.observer.observe(this.summary);

        this.resizeObserver = new ResizeObserver(() => {
          if (this.classList.contains('quick-order-floating-summary--show')) {
            this.updatePageOffset(true);
          }
        });
        this.resizeObserver.observe(this);
      }

      toggle(show) {
        this.classList.toggle('quick-order-floating-summary--show', show);
        this.setAttribute('aria-hidden', show ? 'false' : 'true');
        this.toggleAttribute('inert', !show);
        this.updatePageOffset(show);
      }

      updatePageOffset(show) {
        document.documentElement.style.setProperty(
          '--quick-order-summary-height',
          show ? `${this.offsetHeight}px` : '0px'
        );
        document.body.classList.toggle('quick-order-floating-summary-visible', show);
      }

      disconnectedCallback() {
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
          this.resizeObserver = null;
        }
        document.documentElement.style.setProperty('--quick-order-summary-height', '0px');
        document.body.classList.remove('quick-order-floating-summary-visible');
      }
    }
  );
}

if (!customElements.get('quick-order-list-remove')) {
  customElements.define(
    'quick-order-list-remove',
    class QuickOrderListRemove extends HTMLButtonElement {
      constructor() {
        super();
        this.initializeComponent();
      }

      /**
       * Initialize the remove button component
       */
      initializeComponent() {
        try {
          this.bindEvents();
          this.findParentForm();
        } catch (error) {
          console.error('Error initializing QuickOrderListRemove:', error);
        }
      }

      /**
       * Bind necessary event listeners
       */
      bindEvents() {
        this.handleClick = this.handleClick.bind(this);
        this.addEventListener('click', this.handleClick);
      }

      /**
       * Find and store reference to parent quick order list form
       */
      findParentForm() {
        this.quickOrderList = this.closest('form[is="quick-order-form"]');

        if (!this.quickOrderList) {
          throw new Error('QuickOrderListRemove must be inside a quick-order-form form');
        }
      }

      /**
       * Handle remove button click
       * @param {Event} event - Click event
       */
      handleClick(event) {
        try {
          event.preventDefault();
          const itemIndex = this.getAttribute('data-index');
          if (!itemIndex) {
            throw new Error('Missing data-index attribute');
          }
          this.removeItem(itemIndex);
        } catch (error) {
          console.error('Error handling remove click:', error);
        }
      }

      /**
       * Remove item from cart
       * @param {string} itemIndex - Index of item to remove
       */
      removeItem(itemIndex) {
        const REMOVE_QUANTITY = 0;
        this.quickOrderList.updateItemQuantity(itemIndex, REMOVE_QUANTITY);
      }

      /**
       * Clean up when element is removed
       */
      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.quickOrderList = null;
      }
    },
    { extends: 'button' }
  );
}

if (!customElements.get('quick-order-list-remove-all')) {
  customElements.define(
    'quick-order-list-remove-all',
    class QuickOrderListRemoveAll extends HTMLElement {
      static ACTIONS = {
        CONFIRM: 'confirm',
        REMOVE: 'remove',
        CANCEL: 'cancel',
      };

      static SELECTORS = {
        CONFIRMATION: '.quick-order-list-total__confirmation',
        INFO: '.quick-order-list-total__info',
        VARIANT_QTY: '[data-quantity-variant-id]',
      };

      constructor() {
        super();
        this.handleClick = this.handleClick.bind(this);
        this.initializeComponent();
      }

      /**
       * Initialize the component
       */
      initializeComponent() {
        try {
          this.bindEvents();
          this.findParentForm();
          this.initializeCartItems();
        } catch (error) {
          console.error('Error initializing QuickOrderListRemoveAll:', error);
        }
      }

      /**
       * Bind event listeners
       */
      bindEvents() {
        this.addEventListener('click', this.handleClick);
      }

      /**
       * Find and store parent quick order list form
       */
      findParentForm() {
        this.quickOrderList = this.closest('form[is="quick-order-form"]');

        if (!this.quickOrderList) {
          throw new Error('QuickOrderListRemoveAll must be inside a quick-order-form form');
        }
      }

      /**
       * Initialize cart items data
       */
      initializeCartItems() {
        this.items = {};
        this.hasVariantsInCart = false;

        const variants = this.quickOrderList.querySelectorAll(QuickOrderListRemoveAll.SELECTORS.VARIANT_QTY);

        variants.forEach(this.processVariant.bind(this));
      }

      /**
       * Process each variant element
       * @param {HTMLElement} variant - Variant element
       */
      processVariant(variant) {
        const cartQuantity = parseInt(variant.getAttribute('data-cart-quantity'));
        const variantId = parseInt(variant.getAttribute('data-quantity-variant-id'));

        if (cartQuantity > 0 && !isNaN(variantId)) {
          this.hasVariantsInCart = true;
          this.items[variantId] = 0;
        }
      }

      /**
       * Handle button click events
       * @param {Event} event - Click event
       */
      handleClick(event) {
        try {
          event.preventDefault();
          const action = this.getAttribute('data-action');

          if (!action) {
            throw new Error('Missing data-action attribute');
          }

          this.handleAction(action);
        } catch (error) {
          console.error('Error handling click:', error);
        }
      }

      /**
       * Handle different button actions
       * @param {string} action - Action to perform
       */
      handleAction(action) {
        const actionHandlers = {
          [QuickOrderListRemoveAll.ACTIONS.CONFIRM]: () => {
            this.updateVisibility(false, true);
          },
          [QuickOrderListRemoveAll.ACTIONS.REMOVE]: () => {
            this.removeAllItems();
            this.updateVisibility(true, false);
          },
          [QuickOrderListRemoveAll.ACTIONS.CANCEL]: () => {
            this.updateVisibility(true, false);
          },
        };

        const handler = actionHandlers[action];
        if (handler) {
          handler();
        }
      }

      /**
       * Remove all items from cart
       */
      removeAllItems() {
        FoxTheme.utils.clearCart();
        document.querySelectorAll('.quick-order-list__remove:not(quick-order-list-remove-all)').forEach((button) => {
          button.classList.add('invisible');
          button.classList.add('pointer-events-none');
          button.classList.remove('flex');
        });
      }

      /**
       * Update visibility of confirmation and info sections
       * @param {boolean} showConfirmation - Show confirmation section
       * @param {boolean} showInfo - Show info section
       */
      updateVisibility(showConfirmation, showInfo) {
        const confirmation = this.quickOrderList.querySelector(QuickOrderListRemoveAll.SELECTORS.CONFIRMATION);
        const info = this.quickOrderList.querySelector(QuickOrderListRemoveAll.SELECTORS.INFO);

        if (confirmation && info) {
          confirmation.toggleAttribute('hidden', showConfirmation);
          info.toggleAttribute('hidden', showInfo);
        }
      }

      /**
       * Clean up when element is removed
       */
      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.quickOrderList = null;
        this.items = null;
      }
    }
  );
}
